<template>
  <div class="page-wrapper">
    <div class="page-content">
      <div class="overview" :class="{ 'overflow-y': !isEmptyList }">
        <div>
          <!-- Header -->
          <PublishingListHeader
            v-if="totalCompositionCount"
            @change="getCompositions"
            @create="create"
          />

          <!-- Error State -->
          <WidgetError v-if="loadError" @retry="reload" />

          <!-- Loading State -->
          <LoadingHeart
            v-else-if="!initialized"
            class="loading-heart-container"
          />

          <!-- Empty State -->
          <PublishingEmptyList v-else-if="isEmptyList" @create="create" />

          <!-- Composition List -->
          <div v-else>
            <PublishingEmptySearchResult v-if="unsuccessfulSearch" />
            <div class="list">
              <ul>
                <li v-for="composition in compositions" :key="composition.id">
                  <PublishingTile
                    :composition="composition"
                    display-pos-status
                    :relevant-pos-types="relevantPosTypes"
                    design-products-cta
                    @edit="editComposition(composition.id)"
                    @design-products="designProducts(composition.id)"
                  />
                </li>
              </ul>
            </div>

            <!-- Pagination -->
            <div class="pagination-wrapper">
              <Pagination
                id="design-pagination"
                :total="totalCompositionCount"
                :page-size="pageSize"
                :current-page="page"
                @page-change="onPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStore } from 'vuex';
import { onMounted, ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { COMPOSITION_POS_TYPES } from 'commons/api/models/publishingCore/compositionPosStates';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import PublishCreateDialog from '../create/PublishingCreateDialog.vue';
import PublishingTile from './PublishingTile.vue';
import Pagination from 'commons/pagination/Pagination.vue';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import PublishingListHeader from './PublishingListHeader.vue';
import PublishingEmptySearchResult from './PublishingEmptySearchResult.vue';
import PublishingEmptyList from 'src/app/partnerarea/publishing/list/PublishingEmptyList.vue';
import WidgetError from 'src/app/components/error/WidgetError.vue';
import { usePublishingStore } from 'commons/stores/publishing';
import { useCompositionsStore } from 'commons/stores/compositions';
import { trackCompositionTileDesignProductsClick } from 'commons/publishing/publishingListTracking';

const store = useStore();
const publishingStore = usePublishingStore();
const compositionsStore = useCompositionsStore();
const router = useRouter();
const currentRoute = useRoute();

const initialized = ref(false);
const loadError = ref(false);

const isShopScope = publishingStore.isShopScope;
const totalCompositionCount = computed(
  () => compositionsStore.totalCompositionCount,
);
const unsuccessfulSearch = computed(() => compositionsStore.unsuccessfulSearch);
const searched = computed(() => compositionsStore.searched);
const isEmptyList = computed(
  () => !totalCompositionCount.value && !searched.value,
);

const currentScope = computed(() => publishingStore.currentScope);
const compositions = computed(() => compositionsStore.list);
const page = computed(() => compositionsStore.filter.page);
const pageSize = computed(() => compositionsStore.filter.pageSize);

const relevantPosTypes = computed(() => {
  if (isShopScope) {
    return [COMPOSITION_POS_TYPES.SHOP];
  } else {
    return [COMPOSITION_POS_TYPES.MP];
  }
});

const getCompositions = async () => {
  initialized.value = false;
  loadError.value = false;
  try {
    await compositionsStore.fetchCompositions();
    initialized.value = true;
  } catch (error) {
    console.error('compositionsStore.fetchCompositions failed', error);
    loadError.value = true;
  }
};

const create = async () => {
  await dialogService.openDialog(PublishCreateDialog).then(() => {
    compositionsStore.resetFilter();
    compositionsStore.fetchCompositions();
  });
};

const editComposition = (compositionId) => {
  router.push({
    name: currentScope.value?.routes?.detailPage,
    params: { compositionId },
  });
};

// Products are designed on the detail page, so the CTA only adds the tracking to the tile click.
const designProducts = (compositionId) => {
  trackCompositionTileDesignProductsClick({ scope: currentScope.value?.key });
  editComposition(compositionId);
};

const onPageChange = (page) => {
  compositionsStore.setFilter({ page });
  getCompositions();
};

const updateUrlParams = () => {
  router
    .replace({
      query: null,
    })
    .catch(() => {});
};

const initializeCompositions = async () => {
  const restoreState =
    currentRoute.query.restoreState && compositions.value.length;

  if (!restoreState) {
    compositionsStore.resetFilter();
    await getCompositions();
  } else {
    updateUrlParams();
  }

  if (!loadError.value) {
    initialized.value = true;
  }
};

const reload = async () => {
  loadError.value = false;
  initialized.value = false;

  await store.dispatch('publishingCore/fetchData');

  if (store.state.publishingCore.loadError) {
    loadError.value = true;
    return;
  }

  await initializeCompositions();
};

onMounted(async () => {
  // Triggers the load itself rather than waiting on one the login init may
  // never have started, and always settles, so the list renders an error state
  // with a retry instead of spinning forever (DEV-231297).
  await store.dispatch('publishingCore/ensureDataLoaded');

  if (store.state.publishingCore.loadError) {
    loadError.value = true;
    return;
  }

  await initializeCompositions();
});
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/type';

.overview > div {
  padding: 24px;
  width: 100%;
}

ul {
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  grid-gap: 24px;
  padding: 0 0 24px 0;
  margin: 0;
}

.pagination-wrapper {
  padding: 0 0 24px 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h1 {
    margin: 0 0 16px 0;
  }

  p {
    margin: 0 0 24px 0;
    color: $grey65;
  }

  & > div {
    max-width: 700px;
  }
}

.loading-heart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50%;
}
</style>
